import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, Users, GraduationCap, School, Plus, Settings, LogOut, 
  Search, MoreVertical, Edit, Trash2, UserPlus, BarChart3, Globe,
  CheckCircle, XCircle, Eye, Key, RefreshCw, Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [newSchool, setNewSchool] = useState({
    name: '',
    subtitle: 'Мектеп жетістіктерін басқару жүйесі',
    logo_url: '',
    subdomain: '',
    custom_domain: '',
    primary_color: '#6366f1',
    secondary_color: '#D4A017',
    header_bg_color: '#1B1A39',
    contact_email: '',
    contact_phone: '',
    address: '',
    max_students: 500,
    max_teachers: 50
  });
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    password: '',
    full_name: ''
  });
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [editAdminData, setEditAdminData] = useState({
    username: '',
    password: ''
  });
  const [showMigrate, setShowMigrate] = useState(false);
  const [migrateData, setMigrateData] = useState({
    school_name: 'Qyzylorda BIL',
    subdomain: 'qyzylordaer'
  });

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/superadmin');
      return;
    }
    fetchData();
  }, [user]);

  const checkSubdomain = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }
    setCheckingSubdomain(true);
    try {
      const res = await axios.get(`${API}/school/check-subdomain/${subdomain}`);
      setSubdomainAvailable(res.data.available);
    } catch (error) {
      setSubdomainAvailable(null);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const fetchData = async () => {
    try {
      const [schoolsRes, statsRes] = await Promise.all([
        axios.get(`${API}/super-admin/schools`, { headers: getAuthHeaders() }),
        axios.get(`${API}/super-admin/stats`, { headers: getAuthHeaders() })
      ]);
      setSchools(schoolsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Деректерді жүктеу кезінде қате');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateSchool = async () => {
    if (!newSchool.subdomain) {
      toast.error('Subdomain енгізу міндетті!');
      return;
    }
    if (subdomainAvailable === false) {
      toast.error('Бұл subdomain бос емес!');
      return;
    }
    try {
      await axios.post(`${API}/super-admin/schools`, newSchool, { headers: getAuthHeaders() });
      toast.success('Мектеп сәтті құрылды!');
      setShowAddSchool(false);
      setNewSchool({
        name: '',
        subtitle: 'Мектеп жетістіктерін басқару жүйесі',
        logo_url: '',
        subdomain: '',
        custom_domain: '',
        primary_color: '#6366f1',
        secondary_color: '#D4A017',
        header_bg_color: '#1B1A39',
        contact_email: '',
        contact_phone: '',
        address: '',
        max_students: 500,
        max_teachers: 50
      });
      setSubdomainAvailable(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Мектеп құру кезінде қате');
    }
  };

  const handleCreateAdmin = async () => {
    if (!selectedSchool) return;
    try {
      await axios.post(
        `${API}/super-admin/schools/${selectedSchool.id}/admin`,
        newAdmin,
        { headers: getAuthHeaders() }
      );
      toast.success('Админ сәтті құрылды!');
      setShowAddAdmin(false);
      setNewAdmin({ username: '', password: '', full_name: '' });
      setSelectedSchool(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Админ құру кезінде қате');
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    if (!window.confirm('Мектепті және барлық деректерін жою керек пе?')) return;
    try {
      await axios.delete(`${API}/super-admin/schools/${schoolId}`, { headers: getAuthHeaders() });
      toast.success('Мектеп жойылды');
      fetchData();
    } catch (error) {
      toast.error('Жою кезінде қате');
    }
  };

  const handleUpdateAdminPassword = async () => {
    if (!selectedSchool || !editAdminData.password) {
      toast.error('Жаңа құпия сөзді енгізіңіз');
      return;
    }
    try {
      await axios.put(
        `${API}/super-admin/schools/${selectedSchool.id}/admin/password`,
        { new_password: editAdminData.password },
        { headers: getAuthHeaders() }
      );
      toast.success('Құпия сөз сәтті жаңартылды!');
      setShowEditAdmin(false);
      setEditAdminData({ username: '', password: '' });
      setSelectedSchool(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    }
  };

  const handleUpdateAdminUsername = async () => {
    if (!selectedSchool || !editAdminData.username) {
      toast.error('Жаңа логинді енгізіңіз');
      return;
    }
    try {
      await axios.put(
        `${API}/super-admin/schools/${selectedSchool.id}/admin/username`,
        { new_username: editAdminData.username },
        { headers: getAuthHeaders() }
      );
      toast.success('Логин сәтті жаңартылды!');
      setShowEditAdmin(false);
      setEditAdminData({ username: '', password: '' });
      setSelectedSchool(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    }
  };

  const handleMigrateExistingSchool = async () => {
    if (!migrateData.school_name || !migrateData.subdomain) {
      toast.error('Барлық өрістерді толтырыңыз');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/super-admin/migrate-existing-school`,
        migrateData,
        { headers: getAuthHeaders() }
      );
      toast.success(`${res.data.school_name} мектебі сәтті құрылды! ${res.data.stats.users} пайдаланушы, ${res.data.stats.classes} сынып көшірілді.`);
      setShowMigrate(false);
      setMigrateData({ school_name: 'Qyzylorda BIL', subdomain: 'qyzylordaer' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    }
  };

  const filteredSchools = schools.filter(school => 
    school.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Жүктелуде...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Merit Plus</h1>
                <p className="text-sm text-slate-400">Super Admin Panel</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-slate-300">{user?.full_name}</span>
              <Badge variant="outline" className="border-indigo-500 text-indigo-400">
                Super Admin
              </Badge>
              <Button variant="outline" size="icon" onClick={handleLogout} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-indigo-400">{stats.total_schools}</div>
                <p className="text-xs text-slate-400 mt-1">Мектеп</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-400">{stats.active_schools}</div>
                <p className="text-xs text-slate-400 mt-1">Белсенді</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-blue-400">{stats.total_students}</div>
                <p className="text-xs text-slate-400 mt-1">Оқушы</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-amber-400">{stats.total_teachers}</div>
                <p className="text-xs text-slate-400 mt-1">Мұғалім</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-purple-400">{stats.total_admins}</div>
                <p className="text-xs text-slate-400 mt-1">Админ</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-cyan-400">{stats.total_classes}</div>
                <p className="text-xs text-slate-400 mt-1">Сынып</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-rose-400">{stats.total_records}</div>
                <p className="text-xs text-slate-400 mt-1">Жазба</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Schools Section */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Мектептер
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Барлық тіркелген мектептер
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {/* Migration Button */}
                <Dialog open={showMigrate} onOpenChange={setShowMigrate}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10">
                      <Database className="w-4 h-4 mr-2" />
                      Деректерді көшіру
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-800 border-slate-700 text-white">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Database className="w-5 h-5 text-amber-400" />
                        Бар деректерді жаңа мектепке көшіру
                      </DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <p className="text-sm text-slate-400">
                        Бұл әрекет барлық бар деректерді (пайдаланушылар, сыныптар, жазбалар) жаңа мектепке көшіреді.
                      </p>
                      <div className="space-y-2">
                        <Label>Мектеп атауы *</Label>
                        <Input
                          value={migrateData.school_name}
                          onChange={(e) => setMigrateData({...migrateData, school_name: e.target.value})}
                          placeholder="Qyzylorda BIL"
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Subdomain * (URL)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={migrateData.subdomain}
                            onChange={(e) => setMigrateData({...migrateData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                            placeholder="qyzylordaer"
                            className="bg-slate-700 border-slate-600 flex-1"
                          />
                          <span className="text-slate-400 text-sm">.meritplusbil.org</span>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowMigrate(false)} className="border-slate-600">
                        Болдырмау
                      </Button>
                      <Button onClick={handleMigrateExistingSchool} className="bg-amber-600 hover:bg-amber-700">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Көшіру
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                {/* Add School Button */}
                <Dialog open={showAddSchool} onOpenChange={setShowAddSchool}>
                  <DialogTrigger asChild>
                    <Button className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Мектеп қосу
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Жаңа мектеп қосу</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Мектеп атауы *</Label>
                        <Input
                          value={newSchool.name}
                          onChange={(e) => setNewSchool({...newSchool, name: e.target.value})}
                          placeholder="Мысалы: Qzylorda Er BIL"
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Қосымша жазу</Label>
                        <Input
                          value={newSchool.subtitle}
                          onChange={(e) => setNewSchool({...newSchool, subtitle: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                    </div>
                    
                    {/* Subdomain Field */}
                    <div className="space-y-2">
                      <Label>Subdomain * (URL адресі)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={newSchool.subdomain}
                          onChange={(e) => {
                            const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            setNewSchool({...newSchool, subdomain: val});
                            checkSubdomain(val);
                          }}
                          placeholder="shymkent"
                          className="bg-slate-700 border-slate-600 flex-1"
                        />
                        <span className="text-slate-400 text-sm">.meritplusbil.org</span>
                        {checkingSubdomain && <span className="text-slate-400">⏳</span>}
                        {subdomainAvailable === true && <span className="text-green-400">✓ Бос</span>}
                        {subdomainAvailable === false && <span className="text-red-400">✗ Бос емес</span>}
                      </div>
                      <p className="text-xs text-slate-500">
                        Мысалы: shymkent → shymkent.meritplusbil.org
                      </p>
                    </div>

                    {/* Custom Domain (Optional) */}
                    <div className="space-y-2">
                      <Label>Custom Domain (қосымша)</Label>
                      <Input
                        value={newSchool.custom_domain}
                        onChange={(e) => setNewSchool({...newSchool, custom_domain: e.target.value.toLowerCase()})}
                        placeholder="school.example.com"
                        className="bg-slate-700 border-slate-600"
                      />
                      <p className="text-xs text-slate-500">
                        Өз доменіңіз болса осында жазыңыз (DNS баптау қажет)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Лого URL</Label>
                      <Input
                        value={newSchool.logo_url}
                        onChange={(e) => setNewSchool({...newSchool, logo_url: e.target.value})}
                        placeholder="https://..."
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Негізгі түс</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={newSchool.primary_color}
                            onChange={(e) => setNewSchool({...newSchool, primary_color: e.target.value})}
                            className="w-10 h-10 rounded cursor-pointer"
                          />
                          <Input
                            value={newSchool.primary_color}
                            onChange={(e) => setNewSchool({...newSchool, primary_color: e.target.value})}
                            className="bg-slate-700 border-slate-600"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Қосымша түс</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={newSchool.secondary_color}
                            onChange={(e) => setNewSchool({...newSchool, secondary_color: e.target.value})}
                            className="w-10 h-10 rounded cursor-pointer"
                          />
                          <Input
                            value={newSchool.secondary_color}
                            onChange={(e) => setNewSchool({...newSchool, secondary_color: e.target.value})}
                            className="bg-slate-700 border-slate-600"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Header фоны</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={newSchool.header_bg_color}
                            onChange={(e) => setNewSchool({...newSchool, header_bg_color: e.target.value})}
                            className="w-10 h-10 rounded cursor-pointer"
                          />
                          <Input
                            value={newSchool.header_bg_color}
                            onChange={(e) => setNewSchool({...newSchool, header_bg_color: e.target.value})}
                            className="bg-slate-700 border-slate-600"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Макс. оқушы саны</Label>
                        <Input
                          type="number"
                          value={newSchool.max_students}
                          onChange={(e) => setNewSchool({...newSchool, max_students: parseInt(e.target.value)})}
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Макс. мұғалім саны</Label>
                        <Input
                          type="number"
                          value={newSchool.max_teachers}
                          onChange={(e) => setNewSchool({...newSchool, max_teachers: parseInt(e.target.value)})}
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={newSchool.contact_email}
                          onChange={(e) => setNewSchool({...newSchool, contact_email: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Телефон</Label>
                        <Input
                          value={newSchool.contact_phone}
                          onChange={(e) => setNewSchool({...newSchool, contact_phone: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Мекенжай</Label>
                      <Input
                        value={newSchool.address}
                        onChange={(e) => setNewSchool({...newSchool, address: e.target.value})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddSchool(false)} className="border-slate-600">
                      Болдырмау
                    </Button>
                    <Button onClick={handleCreateSchool} className="bg-indigo-600 hover:bg-indigo-700">
                      Құру
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Мектеп іздеу..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {/* Schools Grid */}
            <div className="grid gap-4">
              {filteredSchools.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Мектеп табылмады</p>
                </div>
              ) : (
                filteredSchools.map((school) => (
                  <div
                    key={school.id}
                    className="bg-slate-700/50 rounded-xl p-4 border border-slate-600 hover:border-indigo-500/50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {school.logo_url ? (
                          <img src={school.logo_url} alt={school.name} className="w-14 h-14 rounded-xl object-contain bg-white p-1" />
                        ) : (
                          <div 
                            className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold"
                            style={{ backgroundColor: school.primary_color }}
                          >
                            {school.name.charAt(0)}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-white text-lg">{school.name}</h3>
                            {school.is_active ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Белсенді
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                <XCircle className="w-3 h-3 mr-1" />
                                Өшірілген
                              </Badge>
                            )}
                          </div>
                          {/* Domain/Subdomain */}
                          {school.subdomain && (
                            <p className="text-xs text-cyan-400 flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {school.subdomain}.meritplusbil.org
                              {school.custom_domain && ` • ${school.custom_domain}`}
                            </p>
                          )}
                          <p className="text-sm text-slate-400">{school.subtitle}</p>
                          {school.admin ? (
                            <p className="text-xs text-indigo-400 mt-1">
                              Админ: {school.admin.full_name} (@{school.admin.username})
                            </p>
                          ) : (
                            <p className="text-xs text-amber-400 mt-1">⚠️ Админ тағайындалмаған</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {/* Stats */}
                        <div className="flex gap-6 text-center">
                          <div>
                            <div className="text-xl font-bold text-blue-400">{school.student_count || 0}</div>
                            <div className="text-xs text-slate-400">Оқушы</div>
                          </div>
                          <div>
                            <div className="text-xl font-bold text-amber-400">{school.teacher_count || 0}</div>
                            <div className="text-xs text-slate-400">Мұғалім</div>
                          </div>
                          <div>
                            <div className="text-xl font-bold text-cyan-400">{school.class_count || 0}</div>
                            <div className="text-xs text-slate-400">Сынып</div>
                          </div>
                        </div>

                        {/* Actions */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-slate-800 border-slate-700">
                            {!school.admin && (
                              <DropdownMenuItem 
                                onClick={() => { setSelectedSchool(school); setShowAddAdmin(true); }}
                                className="text-slate-200 focus:bg-slate-700"
                              >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Админ қосу
                              </DropdownMenuItem>
                            )}
                            {school.admin && (
                              <DropdownMenuItem 
                                onClick={() => { 
                                  setSelectedSchool(school); 
                                  setEditAdminData({ username: school.admin.username, password: '' });
                                  setShowEditAdmin(true); 
                                }}
                                className="text-slate-200 focus:bg-slate-700"
                              >
                                <Key className="w-4 h-4 mr-2" />
                                Админ шифрін өзгерту
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="text-slate-200 focus:bg-slate-700">
                              <Edit className="w-4 h-4 mr-2" />
                              Өзгерту
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteSchool(school.id)}
                              className="text-red-400 focus:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Жою
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Add Admin Dialog */}
      <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Мектеп админін қосу</DialogTitle>
            {selectedSchool && (
              <p className="text-sm text-slate-400">{selectedSchool.name}</p>
            )}
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Аты-жөні *</Label>
              <Input
                value={newAdmin.full_name}
                onChange={(e) => setNewAdmin({...newAdmin, full_name: e.target.value})}
                placeholder="Админнің аты-жөні"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="space-y-2">
              <Label>Login *</Label>
              <Input
                value={newAdmin.username}
                onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                placeholder="Кіру логині"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="space-y-2">
              <Label>Құпия сөз *</Label>
              <Input
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                placeholder="Құпия сөз"
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdmin(false)} className="border-slate-600">
              Болдырмау
            </Button>
            <Button onClick={handleCreateAdmin} className="bg-indigo-600 hover:bg-indigo-700">
              Админ құру
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={showEditAdmin} onOpenChange={setShowEditAdmin}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" />
              Админ деректерін өзгерту
            </DialogTitle>
            {selectedSchool && (
              <p className="text-sm text-slate-400">
                {selectedSchool.name} - {selectedSchool.admin?.full_name}
              </p>
            )}
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Жаңа логин</Label>
              <Input
                value={editAdminData.username}
                onChange={(e) => setEditAdminData({...editAdminData, username: e.target.value})}
                placeholder={selectedSchool?.admin?.username || 'Жаңа логин'}
                className="bg-slate-700 border-slate-600"
              />
              <Button 
                onClick={handleUpdateAdminUsername} 
                variant="outline" 
                size="sm"
                className="w-full border-slate-600 mt-1"
                disabled={!editAdminData.username}
              >
                Логинді өзгерту
              </Button>
            </div>
            <div className="border-t border-slate-700 my-2"></div>
            <div className="space-y-2">
              <Label>Жаңа құпия сөз</Label>
              <Input
                type="password"
                value={editAdminData.password}
                onChange={(e) => setEditAdminData({...editAdminData, password: e.target.value})}
                placeholder="Жаңа құпия сөз"
                className="bg-slate-700 border-slate-600"
              />
              <Button 
                onClick={handleUpdateAdminPassword} 
                variant="outline" 
                size="sm"
                className="w-full border-slate-600 mt-1"
                disabled={!editAdminData.password}
              >
                Құпия сөзді өзгерту
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditAdmin(false)} className="border-slate-600">
              Жабу
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
